import { cardDieCutToShapeConfig } from '@apps/card/utils';
import { Box } from '@withjoy/joykit';
import type { FC, Key, ReactNode } from 'react';
import React, { forwardRef } from 'react';
import type { CSSProperties } from 'react';
import type { DraftFunction, Updater } from 'use-immer';
import type { PageJson } from './PageJson.types';
import { CardDieCut } from './CardDieCut';
import type { EditorMode, SurfaceState } from '../steps/CardDesign/useSurface';
import { ImageLayer } from './ImageLayer';
import type { LayerData } from './Layer.types';
import { QRCodeLayer } from './QRCodeLayer';
import { TextLayer } from './TextLayer';
import { RectangleLayer } from './RectangleLayer';
import { FoilLayer } from './FoilLayer';
import { DEFAULT_CUT_MARGIN_INCHES, PPI } from '../steps/CardDesign/config';
import type { StationeryDraftFormat } from '@graphql/generated-base';
import { usePointerUnitPosition } from './usePointerUnitPosition';
import type { Rendition } from '@apps/card/utils/mediaService';

export interface PageProps {
  key?: Key;
  width: number;
  height: number;
  page: PageJson;
  shape?: CardDieCut;
  format?: StationeryDraftFormat;
  texture?: string;
  imageMask?: {
    color: string;
  };
  rendition?: Rendition;
  fill?: string;
  zoom?: number;
  /**
   * Inline CSS custom properties (`--N`) resolving the selected colorway.
   * Layer fields reference these via `var(--N)`; setting them here keeps the
   * page data tokenized (non-destructive) while the preview renders concrete
   * colors/images. See `useColorways`.
   */
  cssVarStyle?: CSSProperties;
  /**
   * Render QR layers as the placeholder, without generating real QR codes.
   * Set by shopping surfaces (gallery, PDP) — see `QRCodeLayer`.
   */
  forcePlaceholderQRCode?: boolean;
  /**
   * Prefixed onto each layer's index-based key. Layers carry no id, so an editor that removes one
   * changes this to remount the layers after it rather than hand them the removed one's state.
   */
  layerKeyPrefix?: number;
}

export interface PageEditor {
  isActive: (layerIndex: number) => boolean;
  isEditing: (layerIndex: number) => boolean;
  /**
   * Read as a getter rather than passed as a value so the `editor` object stays referentially
   * stable. Layers re-render whenever the surface changes, so this always returns current data.
   */
  getMode: () => EditorMode | undefined;
  updateLayer: (layerIndex: number, df: DraftFunction<LayerData>) => void;
  Selector: FC<{ layer: LayerData; layerIndex: number }>;
  updateSurface: Updater<SurfaceState>;
}

const PassthroughSelector = (props: { layer: LayerData; layerIndex: number; children: ReactNode }) => {
  return <>{props.children}</>;
};

export const Page = forwardRef<HTMLDivElement, PageProps & { editor?: PageEditor }>(
  ({ key, width, height, shape, page, editor, format, texture, fill, imageMask, rendition, zoom = 1, cssVarStyle, forcePlaceholderQRCode, layerKeyPrefix }, ref) => {
    const { unitPosition, elementRef } = usePointerUnitPosition();

    const Selector = editor?.Selector ?? PassthroughSelector;
    return (
      <Box
        ref={ref || elementRef}
        key={key}
        backgroundColor={fill ?? page.fill}
        clipPath={cardDieCutToShapeConfig(shape ?? CardDieCut.rectangle).clipPath}
        width={(page.dimensions?.width ?? width) * PPI}
        height={(page.dimensions?.height ?? height) * PPI}
        overflow="clip"
        position="relative"
        transition="clip-path 0.3s"
        style={{ zoom, ...cssVarStyle }}
      >
        {page.layers.map((layer, i) => {
          return (
            <Selector
              key={layerKeyPrefix === undefined ? i : `${layerKeyPrefix}-${i}`}
              layer={layer}
              layerIndex={i}
            >
              {(() => {
                const lType = layer.type;
                const isActive = layer.editable && (editor?.isActive(i) ?? false);
                if (lType === 'text')
                  return (
                    <TextLayer
                      format={format}
                      layer={layer}
                      editor={editor}
                      layerIndex={i}
                    />
                  );
                if (lType === 'image')
                  return (
                    <ImageLayer
                      mask={imageMask}
                      layer={layer}
                      editor={editor}
                      layerIndex={i}
                      rendition={rendition}
                    />
                  );
                if (lType === 'qrcode')
                  return (
                    <QRCodeLayer
                      format={format}
                      layer={layer}
                      canMove={isActive && editor?.getMode() !== 'edit'}
                      forcePlaceholder={forcePlaceholderQRCode}
                      cssVarStyle={cssVarStyle}
                    />
                  );
                if (lType === 'rectangle') return <RectangleLayer layer={layer} />;
                if (lType === 'foil')
                  return (
                    <FoilLayer
                      layer={layer}
                      pageDimensions={page.dimensions ?? { width, height, cutMargin: DEFAULT_CUT_MARGIN_INCHES }}
                      pointerUnitPosition={unitPosition}
                      rendition={rendition}
                    />
                  );
                throw new Error(`Unknown layer type: ${lType} found in card template`);
              })()}
            </Selector>
          );
        })}
        {texture && (
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              backgroundSize: 'cover',
              backgroundImage: texture,
              opacity: 0.5,
              // Decorative overlay; let clicks reach the link layers beneath it.
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
    );
  },
);
